import ExecutionContext from '../ExecutionContext';
import type { ApplicationChoiceDto } from '../Search/ApplicationChoice/Controller/ApplicationChoiceDto.csharp';
import { AddingDifferentApplicationTypeAttempt } from '~/Features/ApplicationEvents/ApplicationEvents';
import {
    AddingAnotherOptionAttempt,
    AddingExistingItemAttempt,
    CampusApplicationItemAdded,
    CampusApplicationItemRemoved,
    MaxNumberOfChoicesExceeded,
} from '~/Features/CampusApplication/Events/CampusApplicationEvents';
import { MessageBroker } from '~/Features/Messaging/MessageBroker';
import { getStoredOnlineApplicationChoices } from '~/Features/OnlineApplication/OnlineApplicationProcessUtils';
import { CampusAnalytics } from '~/Partials/GoogleTagManager/Analytics/CampusAnalytics';
import {
    CampusApplicationStateStorageKey,
    getStoredCampusApplicationChoices,
    MaxChoicesNumber,
} from './CampusApplicationProcessUtils';

export interface ICampusApplicationProcessState {
    append(item: ApplicationChoiceDto): boolean;

    getAll(): ApplicationChoiceDto[];

    clear(): void;

    remove(id: string): void;

    setAll(choices: ApplicationChoiceDto[]): boolean;

    isItemsLimitReached(): boolean;

    isOnlineApplicationInProgress(): boolean;
}

export const CampusApplicationProcessState: () => ICampusApplicationProcessState = () => {
    const isOnlineApplicationInProgress = () => getStoredOnlineApplicationChoices().length > 0;

    const append = (newChoice: ApplicationChoiceDto): boolean => {
        ExecutionContext.ThrowIfServerSide();

        if (isOnlineApplicationInProgress()) {
            MessageBroker.publish(new AddingDifferentApplicationTypeAttempt());

            return false;
        }

        const currentChoices = getAll();

        for (const choice of currentChoices) {
            if (choice.id === newChoice.id) {
                MessageBroker.publish(new AddingExistingItemAttempt(currentChoices.length));

                return false;
            }

            if (choice.studyCode === newChoice.studyCode && choice.variantCode === newChoice.variantCode) {
                MessageBroker.publish(new AddingAnotherOptionAttempt(currentChoices.length));

                return false;
            }
        }

        if (currentChoices.length === MaxChoicesNumber) {
            MessageBroker.publish(new MaxNumberOfChoicesExceeded(MaxChoicesNumber));

            return false;
        }

        currentChoices.push(newChoice);
        localStorage.setItem(CampusApplicationStateStorageKey, JSON.stringify(currentChoices));

        MessageBroker.publish(new CampusApplicationItemAdded(currentChoices.length));

        // TODO: verify with handleSelectedChoiceChange implementation, considering setAll
        CampusAnalytics.addProducts([newChoice]);

        return true;
    };

    const getAll = getStoredCampusApplicationChoices;

    const clear = () => {
        ExecutionContext.ThrowIfServerSide();
        localStorage.removeItem(CampusApplicationStateStorageKey);
        MessageBroker.publish(new CampusApplicationItemRemoved(0));
    };

    const remove = (id: string) => {
        ExecutionContext.ThrowIfServerSide();
        let currentChoices: ApplicationChoiceDto[] = getAll();

        currentChoices = currentChoices.filter(item => item.id !== id);

        localStorage.setItem(CampusApplicationStateStorageKey, JSON.stringify(currentChoices));
        MessageBroker.publish(new CampusApplicationItemRemoved(currentChoices.length));
    };

    const setAll = (newChoices: ApplicationChoiceDto[]): boolean => {
        ExecutionContext.ThrowIfServerSide();

        const currentChoices = getAll();

        if (newChoices.length >= currentChoices.length && isOnlineApplicationInProgress()) {
            MessageBroker.publish(new AddingDifferentApplicationTypeAttempt());

            return false;
        }

        localStorage.setItem(CampusApplicationStateStorageKey, JSON.stringify(newChoices));
        MessageBroker.publish(new CampusApplicationItemAdded(newChoices.length));

        return true;
    };

    const isItemsLimitReached = () => {
        if (ExecutionContext.IsServerSide()) {
            return false;
        }

        return getAll().length >= MaxChoicesNumber;
    };

    return {
        append,
        getAll,
        clear,
        remove,
        setAll,
        isItemsLimitReached,
        isOnlineApplicationInProgress,
    };
};
